/*
 * ======
 * NOTE
 * ======
 *
 * このファイルは `assets/libs/apis/generated/services/Service.ts` にある
 * 実装をほぼコピーしたもの。必要なメソッドだけコピーする。
 * なぜそれをしなければならないのか、APIクライアントの生成方法などは
 * https://docs.google.com/document/d/1oVgRBHDXtBrGWk92qYbwX1xQzdyim-e8/edit
 * を参照のこと。
 */

import type { CancelablePromise } from './generated/core/CancelablePromise';
import { OpenAPI } from './generated/core/OpenAPI';
import { request as __request } from './generated/core/request';

import type { GetScenesResponse } from './generated/models/GetScenesResponse';
import type { getMemberNotificationsResponse } from './generated/models/getMemberNotificationsResponse';
import type { GetFavoriteListSelectorResponse } from './generated/models/GetFavoriteListSelectorResponse';
import type { GetFavoriteProductsV2Response } from './generated/models/GetFavoriteProductsV2Response';
import type { GetProductOptionsResponse } from './generated/models/GetProductOptionsResponse';
import type { PostFavoriteProductForm } from './generated/models/PostFavoriteProductForm';
import type { ResultOkResponse } from './generated/models/ResultOkResponse';
import type { PatchFavoriteListForm } from './generated/models/PatchFavoriteListForm';
import type { PatchFavoriteListResponse } from './generated/models/PatchFavoriteListResponse';

/**
 * 規定の設定
 */
OpenAPI.BASE = '/api';
OpenAPI.WITH_CREDENTIALS = true;
OpenAPI.CREDENTIALS = 'same-origin';
OpenAPI.HEADERS = {
  'X-Api-Key': 'PMhFJKteWLdy4kt5',
};

/**
 * シーン一覧取得
 * @returns GetScenesResponse successful operation
 * @throws ApiError
 */
export const getScenes = (): CancelablePromise<GetScenesResponse> =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/v1/scenes',
    errors: {
      401: '401 Unauthorized',
      500: '500 Internal Server Error',
    },
  });

/**
 * お知らせ一覧取得
 * ユーザーへのお知らせ一覧レスポンス
 *
 * @returns getMemberNotificationsResponse successful operation
 * @throws ApiError
 */
export const getMemberNotifications = ({
  limit,
  page,
}: {
  /** 取得件数(これ毎にページネーションする) **/
  limit?: number;
  /** ページ番号 **/
  page?: number;
}): CancelablePromise<getMemberNotificationsResponse> =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/v1/member_notifications',
    query: {
      limit,
      page,
    },
    errors: {
      500: '500 Internal Server Error',
    },
  });

/**
 * お気に入り商品一覧取得 V2
 *
 * @returns GetFavoriteProductsV2Response successful operation
 */
export const getFavoriteProductsV2 = ({
  favoriteListId,
  direction,
  limit,
  page,
}: {
  favoriteListId: number;
  direction?: string;
  limit?: number;
  page?: number;
}): CancelablePromise<GetFavoriteProductsV2Response> =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/v2/favorite_products/{favorite_list_id}',
    path: {
      favorite_list_id: favoriteListId,
    },
    query: {
      direction,
      limit,
      page,
    },
    errors: {
      400: '400 Bad Request',
      401: '401 Unauthorized',
      500: '500 Internal Server Error',
    },
  });

/**
 * ウィッシュリスト商品一覧取得
 *
 * @returns GetFavoriteProductsV2Response successful operation
 */
export const getWishlistProducts = ({
  token,
  direction,
  limit,
  page,
}: {
  token: string;
  direction?: string;
  limit?: number;
  page?: number;
}): CancelablePromise<GetFavoriteProductsV2Response> =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/v2/wishlists/{token}/products',
    path: {
      token,
    },
    query: {
      direction,
      limit,
      page,
    },
    errors: {
      400: '400 Bad Request',
      401: '401 Unauthorized',
      500: '500 Internal Server Error',
    },
  });

/**
 * お気に入り商品追加
 */
export const postFavoriteProduct = (body: PostFavoriteProductForm): CancelablePromise<ResultOkResponse> =>
  __request(OpenAPI, {
    method: 'POST',
    url: '/v1/favorite_products',
    body,
    mediaType: 'application/json',
    errors: {
      400: '400 Bad Request',
      401: '401 Unauthorized',
      404: '404 Not Found',
      500: '500 Internal Server Error',
    },
  });

/**
 * お気に入り商品削除
 */
export const deleteFavoriteProduct = ({
  productId,
  isPublished = false,
}: {
  productId: number;
  isPublished?: boolean;
}): CancelablePromise<ResultOkResponse> =>
  __request(OpenAPI, {
    method: 'DELETE',
    url: `/v1/favorite_products/${productId}`,
    query: {
      is_published: isPublished,
    },
    errors: {
      400: '400 Bad Request',
      401: '401 Unauthorized',
      404: '404 Not Found',
      500: '500 Internal Server Error',
    },
  });

/**
 * お気に入りリスト更新
 */
export const patchFavoriteList = ({
  favoriteListId,
  body,
}: {
  favoriteListId: number;
  body: PatchFavoriteListForm;
}): CancelablePromise<PatchFavoriteListResponse> =>
  __request(OpenAPI, {
    method: 'PUT',
    url: '/v1/favorite_lists/{favorite_list_id}',
    path: {
      favorite_list_id: favoriteListId,
    },
    body,
    mediaType: 'application/json',
    errors: {
      400: '400 Bad Request',
      401: '401 Unauthorized',
      404: '404 Not Found',
      500: '500 Internal Server Error',
    },
  });

/**
 * お気に入りリスト選択情報取得
 *
 * @returns GetFavoriteListSelectorResponse successful operation
 */
export const getFavoriteListSelector = ({
  productId,
}: {
  productId: number;
}): CancelablePromise<GetFavoriteListSelectorResponse> =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/v1/favorite_lists/selector/{product_id}',
    path: {
      product_id: productId,
    },
    errors: {
      400: '400 Bad Request',
      401: '401 Unauthorized',
      404: '404 Not Found',
      500: '500 Internal Server Error',
    },
  });

/**
 * 商品オプション情報取得
 *
 * @returns GetProductOptionsResponse successful operation
 */
export const getProductOptions = ({ productId }: { productId: number }): CancelablePromise<GetProductOptionsResponse> =>
  __request(OpenAPI, {
    method: 'GET',
    url: '/v1/products/{product_id}/options',
    path: {
      product_id: productId,
    },
    errors: {
      400: '400 Bad Request',
      404: '404 Not Found',
      500: '500 Internal Server Error',
    },
  });
